import { useRef, useState, type DragEvent } from 'react';
import type { Garment, GarmentCategory, GarmentViewKind } from '@studio/shared';

import { assetUrl } from '../lib/api';
import { CaptureNotes } from './CaptureNotes';
import { CheckIcon, PlusIcon, TrashIcon } from './Icons';
import { GarmentExample } from './GarmentExample';
import { capitalise } from '../lib/appearance';
import { useT } from '../lib/i18n';
import { CATEGORIES, DEFAULT_LAYER, MAX_DETAILS, useGarments, wantsDetailPhoto } from '../lib/useGarments';

/**
 * The extra photographs worth asking for, in the order they pay off.
 *
 * The back is first because the back of a garment is otherwise invented, and
 * invented differently in every frame. The care label is last because it
 * never reaches the renderer — it is read once, for what the fabric is.
 */
const EXTRA_VIEWS: GarmentViewKind[] = ['back', 'side', 'detail', 'label'];

interface Props {
  garments: Garment[];
  onChange: (garments: Garment[]) => void;
}

/**
 * Whether this device has a camera worth offering.
 *
 * Pointer coarseness rather than a user-agent string: what matters is that the
 * thing in the reader's hand can be pointed at a jacket, and that is the
 * property a phone and a tablet share and a laptop does not.
 */
const onCamera =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

export function GarmentUploader({ garments, onChange }: Props) {
  const t = useT();
  const {
    busy,
    error,
    notes,
    clearNotes,
    wardrobe,
    openWardrobe,
    wear,
    destroy,
    upload,
    patch,
    addView,
    noteView,
    removeView,
    remove,
  } = useGarments(garments, onChange);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [browsing, setBrowsing] = useState(false);
  /*
   * Counted rather than flagged: dragging over a child of the drop area fires
   * a leave on the parent, and a boolean flickered off and on as the pointer
   * crossed the button's own label.
   */
  const [dragDepth, setDragDepth] = useState(0);
  const dragging = dragDepth > 0;
  const carriesFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files');

  const browse = async () => {
    setBrowsing((prev) => !prev);
    await openWardrobe();
  };

  const handleFiles = async (files: FileList | File[] | null) => {
    await upload(files);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div
      className={`stack uploader ${dragging ? 'is-dragging' : ''}`}
      onDragEnter={(event) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        setDragDepth((depth) => depth + 1);
      }}
      onDragOver={(event) => {
        if (!carriesFiles(event)) return;
        // Without this the browser opens the photograph in place of the app.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={(event) => {
        if (!carriesFiles(event)) return;
        setDragDepth((depth) => Math.max(0, depth - 1));
      }}
      onDrop={(event) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        setDragDepth(0);
        if (!busy) void handleFiles(event.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => void handleFiles(event.target.files)}
      />

      {/*
       * A second input, with `capture`, so the camera opens directly rather
       * than the gallery. Offered only where there is a camera to open: on a
       * desktop the attribute is ignored and the button would be a second
       * file picker wearing a misleading label.
       */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => void handleFiles(event.target.files)}
      />

      {/*
       * Two different acts, told apart by shape: a dashed area that takes a
       * photograph in, and an ordinary button that opens the list of ones you
       * already have. They used to be two identical filled pills.
       */}
      <button
        type="button"
        className={`dropzone ${dragging ? 'is-over' : ''}`}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        <span className="tile" aria-hidden="true">
          {busy ? <span className="spinner" /> : <PlusIcon />}
        </span>
        <span className="dropzone__text">
          {t(busy ? 'wardrobe.uploading' : dragging ? 'wardrobe.dropHere' : 'wardrobe.add')}
          {!busy && !dragging && !onCamera && <span className="dropzone__hint">{t('wardrobe.orDrag')}</span>}
          {!busy && !dragging && <span className="dropzone__hint">{t('wardrobe.example.front')}</span>}
        </span>
        {/* The photograph to take, drawn: one piece, from the front, on a plain ground. */}
        <span className="dropzone__example" aria-hidden="true">
          <GarmentExample view="front" />
          <small>{t('wardrobe.exampleTag')}</small>
        </span>
      </button>

      <div className="row">
        <button type="button" className="btn btn--ghost grow" onClick={() => void browse()}>
          {t(browsing ? 'wardrobe.hideWardrobe' : 'wardrobe.fromWardrobe')}
        </button>
        {onCamera && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => cameraRef.current?.click()}
          >
            {t('wardrobe.photograph')}
          </button>
        )}
      </div>

      {browsing && (
        <div className="stack stack--sm">
          {!wardrobe ? (
            <p className="faint">{t('wardrobe.loading')}</p>
          ) : wardrobe.length === 0 ? (
            <p className="faint">{t('wardrobe.empty')}</p>
          ) : (
            <div className="closet">
              {wardrobe.map((piece) => {
                const worn = garments.some((g) => g.id === piece.id);
                return (
                  <span key={piece.id} className={`closet__item ${worn ? 'is-on' : ''}`}>
                    <button
                      type="button"
                      className="closet__pick"
                      onClick={() => wear(piece)}
                      aria-pressed={worn}
                    >
                      <span className="closet__photo">
                        <img src={assetUrl(piece.assetId)} alt="" loading="lazy" decoding="async" />
                        {worn && (
                          <span className="closet__check" aria-hidden="true">
                            <CheckIcon />
                          </span>
                        )}
                      </span>
                      <span className="closet__label">{piece.label}</span>
                    </button>
                    <button
                      type="button"
                      className="closet__drop"
                      onClick={() => void destroy(piece)}
                      aria-label={t('wardrobe.deleteForever', { label: piece.label })}
                    >
                      <TrashIcon />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {notes.length > 0 && <CaptureNotes notes={notes} onDismiss={clearNotes} />}

      {error && <div className="banner banner--error">{error}</div>}

      {garments.length === 0 ? (
        <p className="faint">{t('shoot.wardrobeHint')}</p>
      ) : (
        <div className="stack stack--sm">
          {garments
            .slice()
            .sort((a, b) => b.layer - a.layer)
            .map((garment, index) => (
              <div key={garment.id} className="piece">
                <span className="piece__photo">
                  <img src={assetUrl(garment.assetId)} alt={garment.label} />
                  <span className="piece__no">{String(index + 1).padStart(2, '0')}</span>
                </span>
                <div className="piece__body">
                  <div className="piece__top">
                    <input
                      className="piece__name"
                      value={garment.label}
                      placeholder={t('wardrobe.what')}
                      aria-label={t('wardrobe.what')}
                      onChange={(event) => void patch(garment.id, { label: event.target.value })}
                    />
                    <button
                      type="button"
                      className="piece__remove"
                      onClick={() => remove(garment.id)}
                      aria-label={t('wardrobe.remove', { label: garment.label })}
                      title={t('wardrobe.takeOff')}
                    >
                      ×
                    </button>
                  </div>
                  <div className="piece__row">
                    <select
                      className="desc__pill"
                      aria-label={t('wardrobe.kind')}
                      value={garment.category}
                      onChange={(event) =>
                        void patch(garment.id, {
                          category: event.target.value as GarmentCategory,
                          layer: DEFAULT_LAYER[event.target.value as GarmentCategory],
                        })
                      }
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {t(`enum.category.${c}`)}
                        </option>
                      ))}
                    </select>
                    <select
                      className="desc__pill"
                      aria-label={t('wardrobe.layer')}
                      value={garment.layer}
                      onChange={(event) => void patch(garment.id, { layer: Number(event.target.value) })}
                    >
                      <option value={1}>{capitalise(t('wardrobe.layer1'))}</option>
                      <option value={5}>{capitalise(t('wardrobe.layer5'))}</option>
                      <option value={10}>{capitalise(t('wardrobe.layer10'))}</option>
                      <option value={12}>{capitalise(t('wardrobe.layer12'))}</option>
                    </select>
                  </div>
                  <input
                    className="desc__pill desc__input"
                    value={garment.notes}
                    placeholder={t('wardrobe.notesShort')}
                    aria-label={t('wardrobe.notes')}
                    onChange={(event) => void patch(garment.id, { notes: event.target.value })}
                  />
                  {wantsDetailPhoto(garment) && (
                    <p className="piece__hint">{t('wardrobe.detailHintShort')}</p>
                  )}
                  <ViewStrip
                    garment={garment}
                    busy={busy}
                    onAdd={(kind, files) => void addView(garment.id, kind, files)}
                    onRemove={(assetId) => void removeView(garment.id, assetId)}
                    onNote={(assetId, note) => void noteView(garment.id, assetId, note)}
                  />
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * The other sides of one piece.
 *
 * Each button is its own file input so that picking a photograph and saying
 * what it shows is a single action rather than a mode to set first — the
 * commonest use is one tap on "back" and one photograph. Details are the
 * exception: a piece can have several (the zip, a cuff, the print), picked
 * together, each with a word on where it is.
 */
function ViewStrip({
  garment,
  busy,
  onAdd,
  onRemove,
  onNote,
}: {
  garment: Garment;
  busy: boolean;
  onAdd: (kind: GarmentViewKind, files: FileList | null) => void;
  onRemove: (assetId: string) => void;
  onNote: (assetId: string, note: string) => void;
}) {
  const t = useT();
  const views = garment.views ?? [];
  const sides = views.filter((v) => v.kind !== 'detail');
  const details = views.filter((v) => v.kind === 'detail');
  const addable = EXTRA_VIEWS.filter((kind) =>
    kind === 'detail' ? details.length < MAX_DETAILS : !views.some((v) => v.kind === kind),
  );

  return (
    <>
      {details.length > 0 && (
        <ul className="details" aria-label={t('wardrobe.details')}>
          {details.map((view, index) => (
            <li key={view.assetId} className="details__item">
              <img className="details__thumb" src={assetUrl(view.assetId)} alt="" />
              <DetailNote
                note={view.note ?? ''}
                label={t('wardrobe.detailWhereN', { n: index + 1 })}
                onSave={(note) => onNote(view.assetId, note)}
              />
              <button
                type="button"
                className="views__drop"
                onClick={() => onRemove(view.assetId)}
                aria-label={t('wardrobe.removeView', { kind: t('wardrobe.view.detail') })}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {/*
       * One tile per side the piece can be photographed from. A side on file
       * shows its photograph; a side still missing shows the photograph to
       * take — the white t-shirt from that side — with a plus to add it.
       */}
      <div className="slots">
        {EXTRA_VIEWS.map((kind) => {
          const onFile = kind === 'detail' ? undefined : sides.find((v) => v.kind === kind);
          const name = capitalise(t(`wardrobe.view.${kind}`));
          if (onFile) {
            return (
              <span key={kind} className="slot is-on">
                <img className="slot__img" src={assetUrl(onFile.assetId)} alt={name} />
                <span className="slot__name">{name}</span>
                <button
                  type="button"
                  className="slot__drop"
                  onClick={() => onRemove(onFile.assetId)}
                  aria-label={t('wardrobe.removeView', { kind: t(`wardrobe.view.${kind}`) })}
                >
                  ×
                </button>
              </span>
            );
          }
          if (!addable.includes(kind)) return null;
          return (
            <label
              key={kind}
              className={`slot${busy ? ' slot--busy' : ''}`}
              title={t(`wardrobe.example.${kind}`)}
            >
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                multiple={kind === 'detail'}
                disabled={busy}
                onChange={(event) => {
                  onAdd(kind, event.target.files);
                  event.target.value = '';
                }}
              />
              <GarmentExample view={kind} category={garment.category} className="slot__img slot__example" />
              <span className="slot__plus" aria-hidden="true">
                +
              </span>
              <span className="slot__name">{name}</span>
              <span className="sr-only">{t(`wardrobe.example.${kind}`)}</span>
            </label>
          );
        })}
      </div>
    </>
  );
}

/** Where a detail is on the piece, saved when the field is left. */
export function DetailNote({
  note,
  label,
  onSave,
}: {
  note: string;
  label: string;
  onSave: (note: string) => void;
}) {
  const t = useT();
  const [value, setValue] = useState(note);
  return (
    <input
      className="details__note"
      value={value}
      aria-label={label}
      placeholder={t('wardrobe.detailWhere')}
      maxLength={80}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => {
        if (value.trim() !== note) onSave(value.trim());
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
      }}
    />
  );
}
